import React from "react";
import { motion } from "framer-motion";
import ColoredTitle from "./ColoredTitle";

const SKILL_GROUPS = [
  {
    label: "ANALYSIS, QA & DOCUMENTATION",
    items: [
      "Data quality checks",
      "Schema validation (Zod)",
      "Automated testing (Jest, fast-check)",
      "Usability feedback",
      "Workflow documentation",
      "Figma",
    ],
  },
  {
    label: "AI & DATA",
    items: [
      "LLM integration (Gemini, multi-model)",
      "Prompt design",
      "Structuring unstructured data for AI",
      "SQL (PostgreSQL)",
      "Python (pandas, matplotlib)",
      "R (R Markdown)",
    ],
  },
  {
    label: "TECHNICAL & TOOLS",
    items: [
      "TypeScript",
      "JavaScript",
      "React",
      "Node.js",
      "REST APIs",
      "Supabase",
      "AWS",
      "Git / GitHub Actions",
    ],
  },
];

const GRID_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.06 },
  },
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function SkillsSection() {
  return (
    <section
      id="skills"
      className="min-h-screen px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28
        pt-0 pb-16 sm:pb-20 md:pb-24"
    >
      {/* Sticky title */}
      <div className="sticky top-0 z-20 bg-lightBeige pt-16 pb-3 transition-shadow duration-300 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-20 xl:-mx-28 px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28 mb-8 sm:mb-10 md:mb-14 text-center">
        <h2 className="font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
          <ColoredTitle text="Skills." />
        </h2>
      </div>

      {/* Divider */}
      <div className="flex justify-center mb-8 sm:mb-10 md:mb-14">
        <div className="h-[3px] bg-lightBrown rounded-full w-28 sm:w-36 md:w-48 lg:w-64" />
      </div>

      {/* Card grid */}
      <motion.div
        variants={GRID_VARIANTS}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-3"
      >
        {SKILL_GROUPS.map((group) => (
          <motion.div
            key={group.label}
            variants={CARD_VARIANTS}
            className="group flex h-full flex-col gap-4 rounded-2xl border-2 border-lightBrown/40
              bg-lightBeige p-5 sm:p-6 shadow-sm
              transition-[transform,background-color,border-color,box-shadow] duration-200 ease-out
              active:scale-[0.985] md:hover:-translate-y-0.5 md:hover:border-lightBrown
              md:hover:bg-lightBrown/10 md:hover:shadow-md"
          >
            <h3
              className="font-bold tracking-wide text-brown text-base sm:text-lg
                transition-colors duration-200 md:group-hover:text-pink"
            >
              {group.label}
            </h3>

            <div className="flex flex-wrap gap-2">
              {group.items.map((skill) => (
                <span
                  key={skill}
                  className="inline-block max-w-full rounded-full border border-lightBrown/50
                    bg-lightBrown/10 px-2.5 py-1 text-xs sm:text-sm font-medium leading-snug text-midBrown
                    transition-colors duration-200 md:group-hover:border-lightBrown/70
                    md:group-hover:text-brown"
                >
                  {skill}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
